import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, CheckCircle2, XCircle, Pencil, Trash2, ExternalLink,
  FileText, Building2, MapPin, Scale, ChevronRight, TrendingUp, Eye, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_TYPES = [
  {
    key: "cost_guide",
    label: "Cost Guide",
    icon: FileText,
    urlPrefix: "/cost",
    example: "iPhone 15 Pro | Apple | phone | 999",
    placeholder:
      "One per line — label | brand | device_type | rrp_gbp\n" +
      "iPhone 15 Pro | Apple | phone | 999\n" +
      "MacBook Air M3 | Apple | laptop | 1149\n" +
      "Sony WH-1000XM5 | Sony | headphones | 379\n" +
      "PlayStation 5 | Sony | console | 479\n" +
      "GoPro Hero 12 | GoPro | camera | 399",
  },
  {
    key: "comparison",
    label: "Comparison",
    icon: Scale,
    urlPrefix: "/compare",
    example: "AppleCare+",
    placeholder:
      "One competitor per line:\n" +
      "AppleCare+\n" +
      "Samsung Care+\n" +
      "SquareTrade\n" +
      "Argos Extended Warranty\n" +
      "John Lewis Protect",
  },
  {
    key: "location",
    label: "Location",
    icon: MapPin,
    urlPrefix: "/repair",
    example: "Manchester | M1 1AA",
    placeholder:
      "One per line — city | postcode\n" +
      "Manchester | M1 1AA\n" +
      "Birmingham | B1 1BB\n" +
      "Leeds | LS1 1UR\n" +
      "Bristol | BS1 4DJ",
  },
  {
    key: "blog",
    label: "Blog",
    icon: FileText,
    urlPrefix: "/guide",
    example: "Is AppleCare+ worth it in 2026?",
    placeholder:
      "One topic per line:\n" +
      "Is AppleCare+ worth it in 2026?\n" +
      "How to protect your child's first phone\n" +
      "5 signs your battery is on its way out\n" +
      "Water damage: what your insurance won't cover",
  },
];

const STATUS_META = {
  auto_published: { label: "Auto-published", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" },
  pending_review: { label: "Needs review", color: "bg-amber-500/10 text-amber-600 border-amber-500/30" },
  draft: { label: "Draft (low)", color: "bg-slate-500/10 text-slate-500 border-slate-500/30" },
  rejected: { label: "Rejected", color: "bg-rose-500/10 text-rose-600 border-rose-500/30" },
};

function parseSeeds(pageType, text) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.map((line) => {
    const parts = line.split("|").map((p) => p.trim());
    if (pageType === "cost_guide") {
      return {
        label: parts[0] || line,
        brand: parts[1] || null,
        device_type: parts[2] || null,
        rrp_gbp: parts[3] ? parseFloat(parts[3]) : null,
      };
    }
    if (pageType === "location") {
      return { label: parts[0] || line, city: parts[0] || line, postcode: parts[1] || null };
    }
    return { label: parts[0] || line };
  });
}

export default function AdminSeoEngine() {
  const [tab, setTab] = useState("generate"); // generate | queue | pages | stats
  const [pageType, setPageType] = useState("cost_guide");
  const [seedsText, setSeedsText] = useState("");
  const [tone, setTone] = useState("informative");
  const [generating, setGenerating] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [queue, setQueue] = useState([]);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);

  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const currentType = useMemo(
    () => PAGE_TYPES.find((t) => t.key === pageType) || PAGE_TYPES[0],
    [pageType]
  );

  const loadQueue = useCallback(async () => {
    try {
      const r = await api.get("/content/seo/review-queue");
      setQueue(r.data || []);
    } catch (e) {
      toast.error("Failed to load review queue");
    }
  }, []);

  const loadPages = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterStatus) params.review_status = filterStatus;
      const r = await api.get("/content/seo/pages", { params });
      setPages(r.data || []);
    } catch (e) {
      toast.error("Failed to load pages");
    }
  }, [filterCategory, filterStatus]);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/content/seo/stats");
      setStats(r.data);
    } catch (e) {
      toast.error("Failed to load stats");
    }
  }, []);

  useEffect(() => {
    if (tab === "queue") loadQueue();
    if (tab === "pages") loadPages();
    if (tab === "stats") loadStats();
  }, [tab, loadQueue, loadPages, loadStats]);

  const runBulkGenerate = async () => {
    const seeds = parseSeeds(pageType, seedsText);
    if (seeds.length === 0) {
      toast.error("Add at least one seed on its own line.");
      return;
    }
    if (seeds.length > 30) {
      toast.error("Max 30 seeds per batch. Split into multiple runs.");
      return;
    }
    setGenerating(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/content/seo/bulk-generate", {
        page_type: pageType,
        seeds,
        tone,
      });
      setLastResult(data);
      const c = data.counts;
      toast.success(
        `${c.ok}/${c.total} generated — ${c.auto_published} auto-published, ${c.pending_review} need review, ${c.draft} draft`
      );
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk generate failed");
    } finally {
      setGenerating(false);
    }
  };

  const approve = async (page) => {
    try {
      await api.post(`/content/seo/pages/${page.id}/approve`, {});
      toast.success("Approved & published");
      loadQueue();
      loadStats();
    } catch (e) {
      toast.error("Approve failed");
    }
  };

  const reject = async (page) => {
    const reason = window.prompt("Reason for rejecting (optional)");
    try {
      await api.post(`/content/seo/pages/${page.id}/reject`, { reason });
      toast.success("Rejected");
      loadQueue();
      loadStats();
    } catch (e) {
      toast.error("Reject failed");
    }
  };

  const del = async (page) => {
    if (!window.confirm(`Delete "${page.title}"?`)) return;
    try {
      await api.delete(`/content/seo/pages/${page.id}`);
      toast.success("Deleted");
      loadPages();
      loadQueue();
      loadStats();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const openEdit = (page) => {
    setEditing(page);
    setEditForm({
      title: page.title,
      slug: page.slug,
      excerpt: page.excerpt,
      body_html: page.body_html,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
      keywords: (page.keywords || []).join(", "),
      published: page.published,
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/content/seo/pages/${editing.id}`, {
        ...editForm,
        keywords: editForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success("Saved");
      setEditing(null);
      loadQueue();
      loadPages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const publicUrl = (page) => {
    const meta = PAGE_TYPES.find((t) => t.key === page.category);
    return `${meta?.urlPrefix || "/seo"}/${page.slug}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-seo-engine">
      <div>
        <div className="overline text-secondary">Admin · SEO Engine</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight flex items-center gap-3">
          <Sparkles className="w-7 h-7 text-primary" /> Inbound Marketing Engine
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Bulk-generate cost guides, comparisons, location pages and blog articles with Claude. High-confidence
          drafts auto-publish · mid-confidence goes to review · low is a draft you can rewrite.
        </p>
      </div>

      <div className="border-b border-border">
        <nav className="flex gap-1">
          {[
            { k: "generate", label: "Generate", icon: Sparkles },
            { k: "queue", label: "Review queue", icon: FileText },
            { k: "pages", label: "All pages", icon: Building2 },
            { k: "stats", label: "Stats", icon: TrendingUp },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`seo-tab-${t.k}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                tab === t.k
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ---- Generate tab ---- */}
      {tab === "generate" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 space-y-4">
            <div>
              <Label className="text-xs mb-1 block">Page type</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {PAGE_TYPES.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setPageType(t.key)}
                    data-testid={`seo-type-${t.key}`}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      pageType === t.key
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <t.icon className={`w-4 h-4 mb-1 ${pageType === t.key ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="text-sm font-medium">{t.label}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{t.urlPrefix}/…</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs mb-1 block">
                Seeds ({seedsText.split("\n").filter((l) => l.trim()).length}/30)
              </Label>
              <Textarea
                value={seedsText}
                onChange={(e) => setSeedsText(e.target.value)}
                rows={12}
                data-testid="seo-seeds-input"
                className="font-mono text-xs"
                placeholder={currentType.placeholder}
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Format: <code className="text-primary">{currentType.example}</code>
              </div>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs">Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="seo-tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="informative">Informative</SelectItem>
                    <SelectItem value="persuasive">Persuasive</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="expert">Expert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={runBulkGenerate}
                disabled={generating || !seedsText.trim()}
                size="lg"
                data-testid="seo-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate batch</>
                )}
              </Button>
            </div>
            {generating && (
              <div className="text-xs text-muted-foreground">
                Claude is drafting your pages — this usually takes 15-20 seconds per page.
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
            <div className="font-medium text-sm">Last run</div>
            {!lastResult && (
              <div className="text-xs text-muted-foreground">
                Your generation summary will show up here. Auto-publish threshold is 85+ confidence;
                60-84 lands in the review queue; below 60 is saved as a low-quality draft.
              </div>
            )}
            {lastResult && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-emerald-500/10 p-3">
                    <div className="text-2xl font-semibold text-emerald-600">{lastResult.counts.auto_published}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-600/80">Auto-published</div>
                  </div>
                  <div className="rounded-lg bg-amber-500/10 p-3">
                    <div className="text-2xl font-semibold text-amber-600">{lastResult.counts.pending_review}</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-600/80">Need review</div>
                  </div>
                  <div className="rounded-lg bg-slate-500/10 p-3">
                    <div className="text-2xl font-semibold text-slate-500">{lastResult.counts.draft}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500/80">Draft</div>
                  </div>
                </div>
                {lastResult.counts.failed > 0 && (
                  <div className="text-xs text-destructive">
                    {lastResult.counts.failed} failed — see log below
                  </div>
                )}
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {lastResult.results.map((r, i) => (
                    <div key={i} className="text-xs flex items-center justify-between gap-2 py-1 border-b border-border/50">
                      <span className="truncate">{r.seed}</span>
                      {r.status === "ok" ? (
                        <Badge className={STATUS_META[r.review_status]?.color}>
                          {r.confidence}
                        </Badge>
                      ) : (
                        <span className="text-destructive text-[10px]">failed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Review queue tab ---- */}
      {tab === "queue" && (
        <div className="space-y-3">
          {queue.length === 0 && (
            <div className="text-center py-16 text-sm text-muted-foreground">
              Review queue is empty — nothing waiting on your call.
            </div>
          )}
          {queue.map((p) => (
            <div key={p.id} data-testid={`queue-item-${p.id}`}
                 className="rounded-xl border border-amber-500/30 bg-amber-500/[0.03] p-5 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_META[p.review_status]?.color}>
                      Confidence {p.confidence_score}
                    </Badge>
                    <Badge variant="outline">{p.category.replace("_", " ")}</Badge>
                    <span className="text-xs text-muted-foreground truncate">{publicUrl(p)}</span>
                  </div>
                  <div className="mt-2 font-medium">{p.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{p.excerpt}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}
                          data-testid={`queue-edit-${p.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" onClick={() => approve(p)}
                          data-testid={`queue-approve-${p.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(p)}
                          data-testid={`queue-reject-${p.id}`}
                          className="border-rose-500/40 text-rose-600 hover:bg-rose-500/10">
                    <XCircle className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- All pages tab ---- */}
      {tab === "pages" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48" data-testid="pages-filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {PAGE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48" data-testid="pages-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="auto_published">Auto-published</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending_review">Needs review</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-xs text-muted-foreground self-end">
              {pages.length} page{pages.length === 1 ? "" : "s"}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pages.map((p) => (
              <div key={p.id} data-testid={`page-card-${p.id}`}
                   className="rounded-xl border border-border bg-card p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium line-clamp-2">{p.title}</div>
                  <Badge className={STATUS_META[p.review_status]?.color}>{p.confidence_score}</Badge>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {p.category.replace("_", " ")} · {STATUS_META[p.review_status]?.label}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2">{p.excerpt}</div>
                <div className="flex items-center gap-2 pt-1">
                  {p.published && (
                    <a href={publicUrl(p)} target="_blank" rel="noreferrer"
                       className="text-xs text-primary hover:underline flex items-center gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1 ml-auto">
                    <Eye className="w-3 h-3" /> {p.views}
                    <Users className="w-3 h-3 ml-2" /> {p.leads_captured}
                  </span>
                </div>
                <div className="flex gap-1 pt-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  {p.review_status === "pending_review" && (
                    <Button size="sm" onClick={() => approve(p)}
                            className="bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => del(p)}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
            {pages.length === 0 && (
              <div className="col-span-3 text-center text-sm text-muted-foreground py-12">
                No pages match — try generating a batch on the Generate tab.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Stats tab ---- */}
      {tab === "stats" && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatTile label="Total pages" value={stats.totals.total_pages} />
            <StatTile label="Published" value={stats.totals.published} color="text-emerald-600" />
            <StatTile label="Awaiting review" value={stats.totals.pending_review} color="text-amber-600" />
            <StatTile label="Total views" value={stats.traffic.views} icon={Eye} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.entries(stats.categories).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border bg-card p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {k.replace("_", " ")}
                </div>
                <div className="mt-2 font-display text-3xl font-semibold">{v.total}</div>
                <div className="text-xs text-muted-foreground">
                  {v.published} live · {v.pending_review} in queue
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-sm font-medium">Confidence thresholds</div>
            <div className="mt-2 text-xs text-muted-foreground">
              Auto-publish ≥ <b>{stats.config.auto_publish_threshold}</b> ·
              Review queue {stats.config.review_threshold}-{stats.config.auto_publish_threshold - 1} ·
              Draft below {stats.config.review_threshold}.
            </div>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit page</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Slug</Label>
                  <Input value={editForm.slug || ""} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Meta title (SEO)</Label>
                  <Input value={editForm.meta_title || ""} onChange={(e) => setEditForm({ ...editForm, meta_title: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Excerpt</Label>
                <Input value={editForm.excerpt || ""} onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Meta description (SEO)</Label>
                <Textarea rows={2} value={editForm.meta_description || ""}
                          onChange={(e) => setEditForm({ ...editForm, meta_description: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Keywords (comma-separated)</Label>
                <Input value={editForm.keywords || ""} onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Body HTML</Label>
                <Textarea rows={16} value={editForm.body_html || ""}
                          className="font-mono text-xs"
                          onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editForm.published}
                       onChange={(e) => setEditForm({ ...editForm, published: e.target.checked })} />
                Published
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, color = "", icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-2 font-display text-3xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
